import { sourcesApi } from './api';
import {
  ICreateSourceRequest,
  IUpdateSourceRequest,
  SourceItem,
  UrlItem,
} from '../../../models';
import {
  SelectorPreviewRequest,
  SelectorPreviewResponse,
} from '../models/preview';
import { AxiosResponse } from 'axios';

/**
 * Create a new source
 * @param data Source creation data
 * @returns The created source
 */
export const createSource = async (
  data: ICreateSourceRequest
): Promise<SourceItem> => {
  const response: AxiosResponse<SourceItem> = await sourcesApi.create(data);
  return response.data;
};

/**
 * Update an existing source
 * @param id Source ID
 * @param data Source update data
 * @returns The updated source
 */
export const updateSource = async (
  id: string,
  data: IUpdateSourceRequest
): Promise<SourceItem> => {
  const response: AxiosResponse<SourceItem> = await sourcesApi.update(id, data);
  return response.data;
};

/**
 * Delete a source
 * @param id Source ID
 * @returns Success message
 */
export const deleteSource = async (
  id: string
): Promise<{ message: string }> => {
  const response: AxiosResponse<{ message: string }> = await sourcesApi.delete(
    id
  );
  return response.data;
};

/**
 * Get a source by ID
 * @param id Source ID
 * @returns The source
 */
export const getSource = async (id: string): Promise<SourceItem> => {
  const response: AxiosResponse<SourceItem> = await sourcesApi.getById(id);
  return response.data;
};

/**
 * Get all sources
 * @returns List of sources
 */
export const getAllSources = async (): Promise<SourceItem[]> => {
  const response: AxiosResponse<SourceItem[]> = await sourcesApi.getAll();
  return response.data;
};

/**
 * Trigger processing of a source
 * @param id Source ID
 * @returns Success message
 */
export const processSource = async (
  id: string
): Promise<{ message: string }> => {
  const response: AxiosResponse<{ message: string }> = await sourcesApi.process(
    id
  );
  return response.data;
};

/**
 * Reset a source's processing status to IDLE
 * @param id Source ID
 * @returns Success message
 */
export const resetSourceStatus = async (
  id: string
): Promise<{ message: string }> => {
  const response: AxiosResponse<{ message: string }> = await sourcesApi.resetStatus(
    id
  );
  return response.data;
};

/**
 * Get URLs for a source
 * @param id Source ID
 * @param status Optional status filter
 * @returns List of URLs
 */
export const getSourceUrls = async (
  id: string,
  status?: string,
  active?: boolean
): Promise<UrlItem[]> => {
  const response: AxiosResponse<UrlItem[]> = await sourcesApi.getUrls(
    id,
    status,
    active
  );
  return response.data;
};

/**
 * Add URLs to a source
 * @param id Source ID
 * @param urls List of URLs to add
 * @returns Success message
 */
export const addSourceUrls = async (
  id: string,
  urls: string[]
): Promise<{ message: string }> => {
  const response: AxiosResponse<{ message: string }> = await sourcesApi.addUrls(
    id,
    urls
  );
  return response.data;
};

export const previewSelectors = async (
  payload: SelectorPreviewRequest
): Promise<SelectorPreviewResponse> => {
  const response: AxiosResponse<SelectorPreviewResponse> =
    await sourcesApi.previewSelectors(payload);
  return response.data;
};
